import { z } from "zod";
import { customerMemoryControlsSchema, memoryExpectedVersionSchema, memoryFormSchema, memoryKindSchema, memoryProvenanceSchema, memoryReviewReasonSchema, memoryStaffReasonCodeSchema, memoryStatusSchema, memoryValueInputSchema, } from "./customer-memory.js";
// The contract of the staff customer memory surface (CHT-721, design `docs/architecture/phase-5-memory-api.md`
// and `phase-5-memory.md` 10.1): the customer list, the memory workspace of one customer, the detail of one
// fact and the commands staff run on it. FROZEN at the stage 0 commit of lane J. Lane K (admin) builds on it.
//
// Same rules as `staff-catalog.ts`. Every shape is STRICT, times are ISO 8601 text, the API validates bodies
// with these schemas and the admin and the typed client validate the responses with them.
//
// What staff see that a customer does not: the raw and normalized value, status, confidence, confirmation
// level, source type and reference, sensitivity class, review reason, the challenge link, the supersession
// chain, the evidence list (ids, plus the sender, time and conversation of a chat message; CHT-1984), the
// extraction run (prompt version and model key) and the audit trail.
// What NOBODY sees: model rationale, an evidence quote or message text. They are not stored, so no field
// for them exists (CHT-721: a candidate never exposes raw model reasoning).
//
// Authorization is NOT in this package: the use cases read authority from Postgres. A staff member needs the
// capability of the route AND, for one customer's memory, an effective assignment to that customer (or the
// supervisor role). No relationship, an unknown id and a deleted target all answer 403.
// --- parts ------------------------------------------------------------------------------------------
const id = z.guid();
const instant = z.iso.datetime({ offset: true });
const nullableInstant = instant.nullable();
const version = z.number().int().min(1).max(2_147_483_647);
export const staffCustomerRelationshipValues = [
    "PRIMARY",
    "BACKUP",
    "TEMPORARY",
    "SUPERVISOR",
];
export const memorySourceTypeValues = [
    "EXPLICIT_CUSTOMER_STATEMENT",
    "EXPLICIT_REMEMBER_REQUEST",
    "STAFF_ENTRY",
    "APPROVED_EXTRACTION",
    "HISTORICAL_TRIP_OUTCOME",
    "SYSTEM_DERIVED_OBSERVATION",
];
export const memorySourceReferenceKindValues = [
    "CONVERSATION_MESSAGE",
    "CONCIERGE_REQUEST",
    "TRIP",
    "EXTRACTION_RUN",
    "CUSTOMER_APP_ENTRY",
    "STAFF_CONSOLE_ENTRY",
];
export const memorySensitivityValues = ["STANDARD", "PERSONAL", "SENSITIVE"];
export const memoryConfirmationLevelValues = [
    "UNCONFIRMED",
    "STAFF_CONFIRMED",
    "CUSTOMER_CONFIRMED",
];
export const memoryEvidenceEventValues = [
    "ORIGIN",
    "REINFORCE",
    "RETRACTION",
    "SOURCE_EDITED",
    "SOURCE_DELETED",
];
export const memoryActorKindValues = ["customer", "staff", "system"];
/** The source types a staff member may write (`STAFF_ENTRY`; `HISTORICAL_TRIP_OUTCOME` needs a trip or request reference). */
export const staffMemoryRecordSourceTypeValues = [
    "STAFF_ENTRY",
    "HISTORICAL_TRIP_OUTCOME",
];
// --- the customer list -----------------------------------------------------------------------------
/**
 * One row of `GET /staff/customers`: a customer the actor may open the memory of. A concierge sees the
 * customers they have an effective assignment to (`PRIMARY`, `BACKUP`, `TEMPORARY`); a supervisor sees every
 * customer, with the assignment type when they hold one and `SUPERVISOR` otherwise. The name is the
 * customer's preferred name and nothing else of their profile (no email, no phone).
 */
export const staffCustomerSummarySchema = z.strictObject({
    customerId: id,
    preferredName: z.string().min(1).max(120).nullable(),
    relationship: z.enum(staffCustomerRelationshipValues),
});
export const staffCustomerListSchema = z.strictObject({
    customers: z.array(staffCustomerSummarySchema).max(100),
    /** `true` when more customers exist than `limit` returned. */
    truncated: z.boolean(),
});
export const staffCustomerListQuerySchema = z.strictObject({
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
// --- one fact --------------------------------------------------------------------------------------
export const staffMemorySourceReferenceSchema = z.strictObject({
    kind: z.enum(memorySourceReferenceKindValues),
    id: z.string().min(1).max(200),
});
/**
 * One fact as staff read it. `value` and `normalizedValue` are `null` for a tombstone (`DELETED`): the value
 * is gone and the history stays. `labelKey` is the i18n key for a `CODE` value, `null` for `TEXT`.
 * `provenance` tells an inference from a statement (design 3). `confidencePermille` is metadata: it never
 * promotes or retrieves a fact. `contested` is `true` while a review candidate or a newer retraction
 * challenges an ACTIVE fact. `successorFactId` is the fact that superseded this one, or `null` when it has
 * none or lies outside the page.
 */
export const staffMemoryFactSchema = z.strictObject({
    id,
    kind: memoryKindSchema,
    form: memoryFormSchema.nullable(),
    value: z.string().max(1000).nullable(),
    normalizedValue: z.string().max(1000).nullable(),
    labelKey: z.string().min(1).max(96).nullable(),
    status: memoryStatusSchema,
    version,
    provenance: memoryProvenanceSchema,
    confirmationLevel: z.enum(memoryConfirmationLevelValues),
    sourceType: z.enum(memorySourceTypeValues),
    sourceReference: staffMemorySourceReferenceSchema.nullable(),
    sensitivity: z.enum(memorySensitivityValues),
    confidencePermille: z.number().int().min(0).max(1000).nullable(),
    reviewReason: memoryReviewReasonSchema.nullable(),
    conflictsWithFactId: id.nullable(),
    supersedesFactId: id.nullable(),
    successorFactId: id.nullable(),
    contested: z.boolean(),
    evidenceCount: z.number().int().min(0).max(1_000_000),
    policyVersion: z.string().max(32).nullable(),
    createdByKind: z.enum(memoryActorKindValues),
    updatedByKind: z.enum(memoryActorKindValues),
    firstObservedAt: instant,
    lastConfirmedAt: nullableInstant,
    expiresAt: nullableInstant,
    statusChangedAt: instant,
    createdAt: instant,
});
/**
 * A candidate that waits for a decision, with the fact it challenges beside it (an update or a conflict).
 * `challenges` is `null` when the candidate challenges nothing, or the challenged fact is outside the page.
 */
export const staffMemoryReviewItemSchema = z.strictObject({
    candidate: staffMemoryFactSchema,
    challenges: staffMemoryFactSchema.nullable(),
});
// --- the workspace of one customer -----------------------------------------------------------------
export const STAFF_CUSTOMER_MEMORY_VIEW_VERSION = "staff-customer-memory-view.v1";
/**
 * `GET /staff/customers/:customerId/memory`. `active` holds the ACTIVE facts (newest confirmation first),
 * `review` every CANDIDATE with its challenged fact, `disputed` the DISPUTED facts and `history` the closed
 * facts (SUPERSEDED, EXPIRED, DELETED) only when `includeClosed=true`. `controls` is read only for staff.
 * `relationship` is the actor's own relationship to the customer. `truncated` is `true` when the page limit
 * cut the list.
 */
export const staffCustomerMemoryViewSchema = z.strictObject({
    schemaVersion: z.literal(STAFF_CUSTOMER_MEMORY_VIEW_VERSION),
    customer: z.strictObject({
        customerId: id,
        preferredName: z.string().min(1).max(120).nullable(),
    }),
    relationship: z.enum(staffCustomerRelationshipValues),
    controls: customerMemoryControlsSchema,
    active: z.array(staffMemoryFactSchema).max(100),
    review: z.array(staffMemoryReviewItemSchema).max(100),
    disputed: z.array(staffMemoryFactSchema).max(100),
    history: z.array(staffMemoryFactSchema).max(100),
    truncated: z.boolean(),
});
export const staffCustomerMemoryQuerySchema = z.strictObject({
    includeClosed: z.enum(["true", "false"]).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
// --- the detail of one fact ------------------------------------------------------------------------
export const STAFF_MEMORY_FACT_DETAIL_VERSION = "staff-memory-fact-detail.v1";
/**
 * What Postgres knows of the chat message of an evidence row (CHT-1984): which conversation, who wrote it, when, and whether it was
 * edited or removed. NEVER its text: the text lives in Stream, and the memory API has no read of it (ADR 0012, phase-5-memory.md
 * section 3 rule 5). Staff read the text in the conversation, where the access rules of section 10.3 and the join apply.
 */
export const staffMemoryEvidenceMessageSchema = z.strictObject({
    conversationId: id,
    authorKind: z.enum(["CUSTOMER", "LAFAYETTE_AI", "CONCIERGE", "SYSTEM"]),
    sentAt: instant,
    state: z.enum(["PRESENT", "EDITED", "DELETED"]),
});
/** One evidence row: the id of the message or record that supports the fact. An id, never text. */
export const staffMemoryEvidenceSchema = z.strictObject({
    event: z.enum(memoryEvidenceEventValues),
    sourceReferenceKind: z.enum(memorySourceReferenceKindValues),
    sourceReferenceId: z.string().min(1).max(200),
    policyVersion: z.string().max(32),
    /** When the message was sent. */
    observedAt: instant,
    /** When the evidence was recorded. */
    recordedAt: instant,
    /** The chat message behind a `CONVERSATION_MESSAGE` row, when Postgres knows it for this customer. `null` otherwise. */
    message: staffMemoryEvidenceMessageSchema.nullable().default(null),
});
/** The extraction run that wrote a system fact: versions, not text. `null` for a fact a person wrote. */
export const staffMemoryRunSchema = z.strictObject({
    id,
    status: z.enum(["QUEUED", "SUCCEEDED", "FAILED", "SKIPPED"]),
    trigger: z.enum(["CONVERSATION_IDLE", "HANDOFF_RELEASED", "MANUAL_REPROCESS", "EVAL"]),
    policyVersion: z.string().max(32),
    extractorPromptVersion: z.string().max(64),
    modelKey: z.string().max(96),
});
/** One step of the supersession chain, oldest first. The chain holds the fact itself. */
export const staffMemoryChainEntrySchema = z.strictObject({
    id,
    kind: memoryKindSchema,
    status: memoryStatusSchema,
    version,
    provenance: memoryProvenanceSchema,
    supersedesFactId: id.nullable(),
    createdAt: instant,
    statusChangedAt: instant,
});
/** One audit row of the fact: the action name, the kind of actor, the time and the closed reason code. */
export const staffMemoryAuditEntrySchema = z.strictObject({
    action: z.string().min(1).max(96),
    actorKind: z.enum(memoryActorKindValues),
    occurredAt: instant,
    reasonCode: memoryStaffReasonCodeSchema.nullable(),
});
/** `GET /staff/memory/facts/:factId`. The audit list holds the rows that name this fact (newest first, at most 50). */
export const staffMemoryFactDetailSchema = z.strictObject({
    schemaVersion: z.literal(STAFF_MEMORY_FACT_DETAIL_VERSION),
    fact: staffMemoryFactSchema,
    relationship: z.enum(staffCustomerRelationshipValues),
    /** The fact this candidate challenges, when it lies within the customer's first 100 facts. */
    challenges: staffMemoryFactSchema.nullable(),
    /** The candidates that challenge this fact. */
    challengedBy: z.array(staffMemoryFactSchema).max(20),
    evidence: z.array(staffMemoryEvidenceSchema).max(50),
    run: staffMemoryRunSchema.nullable(),
    chain: z.array(staffMemoryChainEntrySchema).max(100),
    audit: z.array(staffMemoryAuditEntrySchema).max(50),
});
// --- staff commands --------------------------------------------------------------------------------
/** M-S4. A staff entry. The customer is the PATH's. `reasonCode` is a closed code. */
export const staffMemoryRecordBodySchema = z.strictObject({
    kind: memoryKindSchema,
    value: memoryValueInputSchema,
    sourceType: z.enum(staffMemoryRecordSourceTypeValues),
    /** Required for `HISTORICAL_TRIP_OUTCOME` (a `TRIP` or `CONCIERGE_REQUEST`); `null` for `STAFF_ENTRY`. */
    sourceReference: z
        .strictObject({ kind: z.enum(["TRIP", "CONCIERGE_REQUEST"]), id: id })
        .nullable(),
    requestedStatus: z.enum(["ACTIVE", "CANDIDATE"]),
    reasonCode: memoryStaffReasonCodeSchema,
});
/** M-S5 (activate), M-S6 (reject), M-S8 (confirm), M-S9 (dispute), M-S10 (resolve) and M-S12 (delete). */
export const staffMemoryVersionReasonBodySchema = z.strictObject({
    expectedVersion: memoryExpectedVersionSchema,
    reasonCode: memoryStaffReasonCodeSchema,
});
/**
 * M-S7. Accepts a candidate that challenges an ACTIVE or DISPUTED fact: the path id is the CANDIDATE.
 * `expectedVersion` is the candidate's version, `expectedConflictVersion` the challenged fact's.
 */
export const staffMemoryAcceptBodySchema = z.strictObject({
    expectedVersion: memoryExpectedVersionSchema,
    expectedConflictVersion: memoryExpectedVersionSchema,
    reasonCode: memoryStaffReasonCodeSchema,
});
/** M-S11. A correction makes a NEW fact (new id) and supersedes the old one; `kind` must equal the fact's kind. */
export const staffMemoryCorrectBodySchema = z.strictObject({
    expectedVersion: memoryExpectedVersionSchema,
    kind: memoryKindSchema,
    value: memoryValueInputSchema,
    reasonCode: memoryStaffReasonCodeSchema,
});
export const staffMemoryCommandNames = [
    "RECORD",
    "ACTIVATE",
    "REJECT",
    "ACCEPT",
    "CONFIRM",
    "DISPUTE",
    "RESOLVE",
    "CORRECT",
    "DELETE",
];
/**
 * What a staff command answers: ids, a status and a version, never a value. `factId` is the fact the command
 * wrote or changed: the NEW fact for RECORD, CORRECT and ACCEPT (the successor), else the path's fact.
 * `status` is the fact's new status, or `null` for CORRECT and ACCEPT (the successor is ACTIVE and its
 * version is `version`). `supersededFactId` is set for CORRECT and ACCEPT; `candidateId` for ACCEPT.
 * The caller re-reads the workspace or the detail.
 */
export const staffMemoryCommandOutcomeSchema = z.strictObject({
    command: z.enum(staffMemoryCommandNames),
    factId: id,
    status: memoryStatusSchema.nullable(),
    version,
    supersededFactId: id.nullable(),
    candidateId: id.nullable(),
});
// --- routes ----------------------------------------------------------------------------------------
const STAFF_COMMON_ERRORS = [
    "401 unauthenticated",
    "403 forbidden (no capability, no relationship, unknown id)",
    "429 rate_limited",
];
const COMMAND_ERRORS = [
    ...STAFF_COMMON_ERRORS,
    "400 invalid_request",
    "409 conflict (memory.version_conflict, memory.transition_invalid, memory.duplicate_open_fact, memory.tombstoned)",
    "413 payload_too_large",
    "415 invalid_request",
    "422 unprocessable (memoryInvalid)",
];
/** The staff memory route table (design `phase-5-memory-api.md`, section 3). `/staff/*` is rate class `staff`. */
export const STAFF_MEMORY_ROUTES = Object.freeze({
    customers: {
        id: "M-S1",
        method: "GET",
        path: "/staff/customers",
        authorization: "customer.read_assigned or customer.read_all",
        request: "staffCustomerListQuerySchema (query)",
        response: "staffCustomerListSchema",
        success: 200,
        idempotent: false,
        errors: [...STAFF_COMMON_ERRORS, "400 invalid_request"],
    },
    workspace: {
        id: "M-S2",
        method: "GET",
        path: "/staff/customers/:customerId/memory",
        authorization: "memory.read + effective assignment or supervisor",
        request: "staffCustomerMemoryQuerySchema (query)",
        response: "staffCustomerMemoryViewSchema",
        success: 200,
        idempotent: false,
        errors: [...STAFF_COMMON_ERRORS, "400 invalid_request"],
    },
    detail: {
        id: "M-S3",
        method: "GET",
        path: "/staff/memory/facts/:factId",
        authorization: "memory.read + effective assignment or supervisor",
        request: null,
        response: "staffMemoryFactDetailSchema",
        success: 200,
        idempotent: false,
        errors: [...STAFF_COMMON_ERRORS, "400 invalid_request"],
    },
    record: {
        id: "M-S4",
        method: "POST",
        path: "/staff/customers/:customerId/memory/facts",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryRecordBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 201,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
    activate: {
        id: "M-S5",
        method: "POST",
        path: "/staff/memory/facts/:factId/activate",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryVersionReasonBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
    reject: {
        id: "M-S6",
        method: "POST",
        path: "/staff/memory/facts/:factId/reject",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryVersionReasonBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
    accept: {
        id: "M-S7",
        method: "POST",
        path: "/staff/memory/facts/:factId/accept",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryAcceptBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
    confirm: {
        id: "M-S8",
        method: "POST",
        path: "/staff/memory/facts/:factId/confirm",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryVersionReasonBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
    dispute: {
        id: "M-S9",
        method: "POST",
        path: "/staff/memory/facts/:factId/dispute",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryVersionReasonBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
    resolve: {
        id: "M-S10",
        method: "POST",
        path: "/staff/memory/facts/:factId/resolve",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryVersionReasonBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
    correct: {
        id: "M-S11",
        method: "POST",
        path: "/staff/memory/facts/:factId/correct",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryCorrectBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
    delete: {
        id: "M-S12",
        method: "POST",
        path: "/staff/memory/facts/:factId/delete",
        authorization: "memory.manage + effective assignment or supervisor",
        request: "staffMemoryVersionReasonBodySchema",
        response: "staffMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [...COMMAND_ERRORS],
    },
});
export const staffMemoryFactSubpaths = [
    "activate",
    "reject",
    "accept",
    "confirm",
    "dispute",
    "resolve",
    "correct",
    "delete",
];
export const STAFF_MEMORY_PATHS = Object.freeze({
    customers: STAFF_MEMORY_ROUTES.customers.path,
});
export const staffCustomerMemoryPath = (customerId) => `/staff/customers/${encodeURIComponent(customerId)}/memory`;
export const staffCustomerMemoryFactsPath = (customerId) => `${staffCustomerMemoryPath(customerId)}/facts`;
export const staffMemoryFactPath = (factId, subpath) => `/staff/memory/facts/${encodeURIComponent(factId)}${subpath === undefined ? "" : `/${subpath}`}`;
